import {
  AlertCircle,
  ArrowRightLeft,
  AtSign,
  CheckCircle2,
  KeyRound,
  Loader2,
  RefreshCw,
  ShieldAlert,
} from "lucide-react";
import { useId, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaInput } from "@/shared/components/forms/ColocaInput";
import { ColocaLabel } from "@/shared/components/forms/ColocaLabel";
import { ColocaDialog, ColocaDialogClose } from "@/shared/components/overlays/ColocaDialog";
import { TwoFactorActionModals } from "@/shared/components/overlays/TwoFactorActionModals";
import { BrebKeyDeleteAction } from "@/shared/components/payments/breb-key/BrebKeyFlow";
import { BrebKeyPlate } from "@/shared/components/payments/breb-key/BrebKeyPlate";
import { BREB_KEY_SOURCE_NAME } from "@/shared/components/payments/breb-key/brebKeySources";
import { useBrebReceivingKey } from "@/shared/hooks/useBrebReceivingKey";
import { useTwoFactorAction } from "@/shared/hooks/useTwoFactorAction";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import {
  brebKeyDeleteImpact,
  type BrebReceivingKey,
  BrebReceivingKeyService,
} from "@/shared/services/payments/brebReceivingKey.service";
import { colocaToast } from "@/shared/utils/toast";

import { type CustomKeyCheck, useCustomBrebKeyAvailability } from "../hooks/useCustomBrebKeyAvailability";
import type { AdminOrganization } from "../types/organizationsAdmin.types";

const PREVIEW_PLACEHOLDER = "@…";

export interface AssignCustomBrebKeyDialogProps {
  /** The organization receiving the key; null closes the dialog. */
  organization: AdminOrganization | null;
  onOpenChange: (open: boolean) => void;
  /** Offered on each ACTIVE key; the host closes this dialog and opens the transfer. */
  onTransferKey?: (key: BrebReceivingKey) => void;
}

export function AssignCustomBrebKeyDialog({
  organization,
  onOpenChange,
  onTransferKey,
}: AssignCustomBrebKeyDialogProps) {
  if (!organization) return null;
  return (
    <AssignCustomBrebKeyDialogContent
      key={organization.id}
      organization={organization}
      onClose={() => onOpenChange(false)}
      onTransferKey={onTransferKey}
    />
  );
}

interface ContentProps {
  organization: AdminOrganization;
  onClose: () => void;
  onTransferKey?: (key: BrebReceivingKey) => void;
}

function AssignCustomBrebKeyDialogContent({ organization, onClose, onTransferKey }: ContentProps) {
  const { t } = useTranslations("organizations");
  const inputId = useId();
  const hintId = useId();
  const keyState = useBrebReceivingKey(organization.id);
  const twoFactor = useTwoFactorAction<BrebReceivingKey>();
  const [value, setValue] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const { check, recheck } = useCustomBrebKeyAvailability(value);

  const state = keyState.state;
  const eligible = !!state && state.eligibility.verified && state.eligibility.rail_enabled;
  const available = check.status === "done" && check.result.availability === "AVAILABLE";
  const preview = check.status === "empty" || check.status === "invalid" ? null : check.keyValue;

  const assign = async () => {
    if (check.status !== "done" || !available) return;
    const keyValue = check.keyValue;
    setAssigning(true);
    setAssignError(null);
    try {
      const created = await twoFactor.start((code) =>
        BrebReceivingKeyService.assignCustom(organization.id, keyValue, code),
      );
      if (!created) return;
      keyState.reload();
      colocaToast.success(
        created.status === "PENDING"
          ? t("custom_key_assigned_pending_toast", { key: created.key_value, name: organization.name })
          : t("custom_key_assigned_toast", { key: created.key_value, name: organization.name }),
      );
      onClose();
    } catch (error) {
      setAssignError(HttpApiService.getErrorMessage(error));
      recheck();
      keyState.reload();
    } finally {
      setAssigning(false);
    }
  };

  return (
    <>
      <ColocaDialog
        open
        onOpenChange={(open) => {
          if (!open && !assigning) onClose();
        }}
        size="lg"
        title={t("custom_key_title", { name: organization.name })}
        description={t("custom_key_description")}
        showCloseButton={!assigning}
        footer={
          <>
            <ColocaDialogClose render={<ColocaButton variant="ghost" disabled={assigning} />}>
              {t("custom_key_cancel")}
            </ColocaDialogClose>
            {eligible && (
              <ColocaButton
                variant="primary"
                icon={KeyRound}
                loading={assigning}
                disabled={!available || assigning}
                onClick={() => void assign()}
              >
                {t("custom_key_assign")}
              </ColocaButton>
            )}
          </>
        }
      >
        {!state && keyState.isLoading && (
          <div className="space-y-3" aria-busy="true">
            <ColocaSkeleton className="h-10 w-full" />
            <ColocaSkeleton className="h-36 w-full rounded-3xl" />
          </div>
        )}

        {!state && !keyState.isLoading && (
          <ColocaAlert
            variant="destructive"
            icon={AlertCircle}
            text={t("custom_key_load_error")}
            actions={
              <ColocaButton size="sm" icon={RefreshCw} onClick={keyState.reload}>
                {t("custom_key_retry")}
              </ColocaButton>
            }
          />
        )}

        {state && state.keys.length > 0 && (
          <OrganizationKeys
            organizationName={organization.name}
            keys={state.keys}
            canCreateAgain={state.eligibility.enabled}
            onDelete={keyState.remove}
            onTransfer={assigning ? undefined : onTransferKey}
          />
        )}

        {state && !state.eligibility.verified && (
          <ColocaAlert variant="warning" icon={ShieldAlert} text={t("custom_key_not_verified")} />
        )}

        {state && state.eligibility.verified && !state.eligibility.rail_enabled && (
          <ColocaAlert variant="warning" icon={ShieldAlert} text={t("custom_key_rail_disabled")} />
        )}

        {eligible && (
          <div className="space-y-5">
            {state.keys.length > 0 && (
              <p className="text-sm font-semibold text-foreground">{t("custom_key_assign_another")}</p>
            )}
            <div className="space-y-2">
              <ColocaLabel htmlFor={inputId}>{t("custom_key_input_label")}</ColocaLabel>
              <div className="relative">
                <AtSign
                  className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden="true"
                />
                <ColocaInput
                  id={inputId}
                  value={value}
                  onValueChange={(next) => {
                    setValue(next);
                    setAssignError(null);
                  }}
                  placeholder={t("custom_key_input_placeholder")}
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  autoFocus
                  maxLength={64}
                  aria-describedby={hintId}
                  aria-invalid={check.status === "invalid" || undefined}
                  disabled={assigning}
                  className="pl-9 font-mono"
                />
              </div>
              <div id={hintId} className="flex min-h-7 flex-wrap items-center gap-2" aria-live="polite">
                <CustomKeyStatus check={check} onRecheck={recheck} />
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">{t("custom_key_preview_label")}</p>
              <BrebKeyPlate
                keyValue={preview ?? PREVIEW_PLACEHOLDER}
                dimmed={!available}
                showActions={false}
              />
              <p className="text-xs text-muted-foreground">{t("custom_key_rollout_note")}</p>
            </div>

            {assignError && <ColocaAlert variant="destructive" icon={AlertCircle} text={assignError} />}
          </div>
        )}
      </ColocaDialog>
      <TwoFactorActionModals
        controller={twoFactor}
        requiredDescription={t("custom_key_two_factor_required")}
        challengeDescription={
          check.status === "done"
            ? t("custom_key_two_factor_description", { key: check.keyValue, name: organization.name })
            : undefined
        }
      />
    </>
  );
}

interface OrganizationKeysProps {
  organizationName: string;
  keys: BrebReceivingKey[];
  canCreateAgain: boolean;
  onDelete: (keyId: string, acceptLiveQrs?: boolean, twoFactorCode?: string) => Promise<void>;
  onTransfer?: (key: BrebReceivingKey) => void;
}

function OrganizationKeys({
  organizationName,
  keys,
  canCreateAgain,
  onDelete,
  onTransfer,
}: OrganizationKeysProps) {
  const { t } = useTranslations("organizations");
  const { t: tPayins } = useTranslations("payins");
  return (
    <section className="mb-5 space-y-2">
      <p className="text-xs font-medium text-muted-foreground">
        {t("custom_key_keys_title", { name: organizationName })}
      </p>
      <ul className="divide-y divide-border rounded-2xl border border-border">
        {keys.map((key) => {
          const impact = brebKeyDeleteImpact(keys, key.id);
          return (
            <li key={key.id} className="flex items-center gap-3 p-3">
              <div className="min-w-0 flex-1">
                <p className="truncate font-mono text-sm font-semibold text-foreground">{key.key_value}</p>
                <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                  {tPayins(BREB_KEY_SOURCE_NAME[key.source])}
                  {key.is_primary && (
                    <ColocaBadge variant="success">{tPayins("breb_key_primary_badge")}</ColocaBadge>
                  )}
                  {key.status === "PENDING" && (
                    <ColocaBadge variant="secondary">{tPayins("breb_key_pending_badge")}</ColocaBadge>
                  )}
                </p>
              </div>
              {onTransfer && key.status === "ACTIVE" && (
                <ColocaIconButton
                  size="sm"
                  variant="ghost"
                  icon={ArrowRightLeft}
                  aria-label={t("custom_key_transfer_action_aria", { key: key.key_value })}
                  title={t("custom_key_transfer_action")}
                  onClick={() => onTransfer(key)}
                />
              )}
              <BrebKeyDeleteAction
                compact
                keyValue={key.key_value}
                canCreateAgain={canCreateAgain}
                outcome={impact.outcome}
                successorValue={impact.successorValue}
                onDelete={(acceptLiveQrs, twoFactorCode) => onDelete(key.id, acceptLiveQrs, twoFactorCode)}
              />
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function CustomKeyStatus({ check, onRecheck }: { check: CustomKeyCheck; onRecheck: () => void }) {
  const { t } = useTranslations("organizations");

  switch (check.status) {
    case "empty":
      return <span className="text-xs text-muted-foreground">{t("custom_key_format_hint")}</span>;
    case "invalid":
      return <span className="text-xs text-destructive">{t("custom_key_invalid")}</span>;
    case "checking":
      return (
        <ColocaBadge variant="secondary" icon={Loader2}>
          {t("custom_key_checking")}
        </ColocaBadge>
      );
    case "error":
      return (
        <>
          <span className="text-xs text-destructive">{check.message}</span>
          <ColocaButton variant="ghost" size="xs" icon={RefreshCw} onClick={onRecheck}>
            {t("custom_key_check_again")}
          </ColocaButton>
        </>
      );
    case "done": {
      const { availability, taken_in_coloca } = check.result;
      if (availability === "AVAILABLE") {
        return (
          <ColocaBadge variant="success" icon={CheckCircle2}>
            {t("custom_key_available")}
          </ColocaBadge>
        );
      }
      if (availability === "TAKEN") {
        return (
          <ColocaBadge variant="destructive">
            {taken_in_coloca ? t("custom_key_taken_coloca") : t("custom_key_taken_elsewhere")}
          </ColocaBadge>
        );
      }
      return (
        <>
          <ColocaBadge variant="warning">{t("custom_key_unknown")}</ColocaBadge>
          <ColocaButton variant="ghost" size="xs" icon={RefreshCw} onClick={onRecheck}>
            {t("custom_key_check_again")}
          </ColocaButton>
        </>
      );
    }
  }
}
